import { useEffect, useRef, useState } from "react";

interface LazyNarrativeFrameProps {
  src: string;
  title: string;
  /** Classes for the wrapper; the iframe fills it. */
  className?: string;
  /** False for decorative embeds that must not take focus or clicks. */
  interactive?: boolean;
}

/**
 * An embedded narrative player that only exists while it's near the
 * viewport. Each embed is a full app instance and keeps animating while
 * mounted (same-origin iframes aren't reliably throttled off-screen), so
 * it mounts within about a screen of view and unmounts once it's further
 * away. Scrolling back remounts it from its start page.
 */
export const LazyNarrativeFrame = ({
  src,
  title,
  className = "",
  interactive = true,
}: LazyNarrativeFrameProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isNear, setIsNear] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsNear(entry.isIntersecting),
      // One viewport height of lead, in pixels: percentage rootMargins
      // resolve against different axes in different browsers.
      { rootMargin: `${window.innerHeight}px 0px` },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    // Decorative embeds are inert, which also hides them from assistive tech.
    <div ref={ref} className={className} inert={!interactive}>
      {isNear && (
        <iframe
          src={src}
          title={title}
          className={`w-full h-full border-none block ${interactive ? "" : "pointer-events-none"}`}
          tabIndex={interactive ? undefined : -1}
        />
      )}
    </div>
  );
};
